import { useCallback, useEffect, useRef, useState } from 'react';
import { focusFirstInvalidInput } from '../helpers/formValidationBehavior';
import * as validationMethods from '../helpers/formValidationMethods';

/**
 * @param {string | Validator} method
 * @returns {Validator}
 */
export function resolveValidationMethod(method) {
  if (typeof method === 'function') {
    return method;
  }
  if (typeof method !== 'string') {
    throw new TypeError(`Validation method must be a string or function; was '${typeof method}'`);
  }
  const resolvedFn = validationMethods[method];
  if (typeof resolvedFn === 'undefined') {
    throw new Error(`Validation method '${method}' is not defined`);
  }
  if (resolvedFn.length > 1) {
    throw new Error(`The validation method '${method}' cannot be resolved because it accepts multiple arguments.`);
  }
  return resolvedFn;
}

/**
 * @param {ValidationInit} item
 * @returns {ValidationTrigger[]}
 */
function getDefaultTriggers(_ref) {
  let {
    errorLevel
  } = _ref;
  if (errorLevel === 'warning') {
    return ['blur'];
  } else {
    return ['changeAfterSubmit'];
  }
}

/**
 * @param {ValidationTrigger[]} matches
 * @param {Validation} validation
 * @returns {boolean}
 */
function shouldTrigger(matches, validation) {
  return validation.when.some(trigger => matches.includes(trigger));
}

/**
 * @param {FormValidationSchema} schema
 * @param {FormValidationOptions} [options]
 * @returns {Validation[]}
 */
export function validationsFromSchema(schema) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return Object.entries(schema).flatMap(_ref2 => {
    let [name, items] = _ref2;
    return items.map(item => {
      let triggers = item.when ? [].concat(item.when) : getDefaultTriggers(item);
      if (options[name]?.disableOnChange) {
        triggers = triggers.filter(trigger => {
          return trigger !== 'changeAfterSubmit' && trigger !== 'change';
        });
      }
      const validationMethod = resolveValidationMethod(item.validationMethod);
      return {
        name,
        validationMethod,
        errorMessage: item.errorMessage || validationMethod.errorMessage || 'An error occurred',
        errorLevel: item.errorLevel || 'error',
        when: triggers
      };
    });
  });
}

/**
 * Run the given validations against the form data and return the results
 * @param {Validation[]} validations
 * @param {FormData} formData
 * @param {FormValidationOptions} options
 * @returns {Promise<{ errors: ValidationErrorMap, warnings: ValidationErrorMap }>}
 */
async function validate(validations, formData, options) {
  /** @type {ValidationErrorMap} */
  const errors = {};
  /** @type {ValidationErrorMap} */
  const warnings = {};
  for (const {
    name,
    validationMethod,
    errorMessage,
    errorLevel
  } of validations) {
    const map = errorLevel === 'error' ? errors : warnings;
    const disableMultipleErrors = options[name]?.disableMultipleErrors;
    const hasRequiredError = map[name]?.hasRequiredError;
    if (disableMultipleErrors) {
      if (errorLevel === 'error' && errors[name]) continue;
      if (errorLevel === 'warning' && warnings[name]) continue;
    } else if (hasRequiredError) {
      // if the input already has a failed required error, skip other validations
      continue;
    }
    let value = formData.getAll(name);
    if (value.length <= 1) {
      value = formData.get(name);
    }
    let ok = false;
    const isRequiredError = validationMethod.name === 'required';
    if (Array.isArray(value) && validationMethod.useGroupValidation) {
      // For grouped input value validation (e.g. multiple files in a file input),
      // we pass the raw value from `formData.getAll` to the validation method.
      ok = await Promise.resolve(validationMethod(value));
    } else if (Array.isArray(value)) {
      // By default, if there are multiple values with the same input name,
      // we require all of the values to pass the validation method.
      const results = await Promise.all(value.map(v => Promise.resolve(validationMethod(v))));
      ok = results.every(result => result);
    } else {
      ok = await Promise.resolve(validationMethod(value));
    }
    if (map[name] && map[name] !== null) {
      if (!ok) {
        // append additional error message if the validation fails
        // if this is a required error, replace any existing error messages
        map[name] = {
          ...map[name],
          errorMessage: isRequiredError ? errorMessage : [].concat(map[name].errorMessage, errorMessage),
          hasRequiredError: isRequiredError
        };
      }
      // no-op if validation passes (keep other error messages)
    } else {
      // add first error message or set as null if validation passes
      map[name] = ok ? null : {
        name,
        errorMessage,
        errorLevel,
        hasRequiredError: isRequiredError
      };
    }
  }
  return {
    errors,
    warnings
  };
}

/**
 * useFormValidation hook for form validation.
 * @param {FormValidationConfig} config - The validation parameters.
 * @returns {FormValidationBag} The form validation methods.
 */
const useFormValidation = _ref3 => {
  let {
    schema,
    onValidate = focusFirstInvalidInput,
    options = {}
  } = _ref3;
  /** @type {import('react').MutableRefObject<HTMLFormElement>} */
  const formRef = useRef();

  // We keep the validations in a ref because we'll later reference them
  // in an effect. We don't want to re-run that effect whenever the
  // validations change, since they're likely to change with every render.
  // We just want to grab a reference to the latest validations and use
  // them when needed.
  const validations = useRef(validationsFromSchema(schema, options));
  useEffect(() => {
    validations.current = validationsFromSchema(schema, options);
  }, [schema, options]);

  // The revalidation queue is either a list of fields to revalidate, or
  // the literal string "*", meaning revalidate all fields.
  const [revalidationQueue, setRevalidationQueue] = useState(/** @type {'*' | string[]} */[]);
  const [errors, setErrors] = useState(/** @type {ValidationErrorMap} */{});
  const [warnings, setWarnings] = useState(/** @type {ValidationErrorMap} */{});
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState(false);

  /**
   * @param {string} inputName
   * @returns {ValidationError | null}
   */
  const findError = inputName => {
    return errors[inputName] || warnings[inputName] || null;
  };

  /**
   * @param {string} inputName
   * @returns {boolean} - if input has error
   */
  const hasError = inputName => Boolean(findError(inputName));

  /**
   * @param {string} inputName
   * @returns {string} - error message
   */
  const errorMessage = inputName => {
    const error = findError(inputName);
    return error && error.errorMessage || '';
  };

  /**
   * @param {string} inputName
   * @returns {ErrorLevel}
   */
  const errorLevel = inputName => {
    const error = findError(inputName);
    return error && error.errorLevel || 'error';
  };
  const run = useCallback(
  /**
   * @param {Validation[]} validations
   * @param {FormData} formData
   * @returns {Promise<Omit<ValidationResult, 'formElement' | 'formData'>>}
   */
  (validations, formData) => {
    return validate(validations, formData, options).then(result => {
      setErrors(currentErrors => ({
        ...currentErrors,
        ...result.errors
      }));
      setWarnings(currentWarnings => ({
        ...currentWarnings,
        ...result.warnings
      }));
      const errors = Object.values(result.errors).filter(e => e !== null);
      const warnings = Object.values(result.warnings).filter(w => w !== null);
      const isValid = errors.length === 0;
      return {
        isValid,
        errors,
        warnings
      };
    });
  }, []);

  /**
   * @param {HTMLInputElement} input
   * @param {ValidationTrigger[]} triggers
   */
  const validateInput = (input, triggers) => {
    const {
      name
    } = input;
    const formData = new FormData(input.form);
    run(validations.current.filter(v => v.name === name && shouldTrigger(triggers, v)), formData);
  };

  /**
   * @param {import('react').ChangeEvent<HTMLInputElement>} e - The change event.
   * @returns {void}
   */
  const onChange = e => {
    validateInput(e.target, hasFormBeenSubmitted ? ['change', 'changeAfterSubmit'] : ['change']);
  };

  /**
   * @param {import('react').FocusEvent<HTMLInputElement>} e - The blur event.
   * @returns {void}
   */
  const onBlur = e => {
    if (e.target.name) {
      validateInput(e.target, ['blur']);
    }
  };

  /**
   * @param {import('react').FormEvent<HTMLFormElement>} e - The form event.
   * @param {import('react').FormEventHandler<HTMLFormElement>} [submitHandler] - The submit event handler.
   */
  const onSubmit = (e, submitHandler) => {
    e.preventDefault();
    setHasFormBeenSubmitted(true);
    const formElement = e.target;
    const formData = new FormData(formElement);
    run(validations.current, formData).then(_ref4 => {
      let {
        isValid,
        errors,
        warnings
      } = _ref4;
      if (onValidate) {
        onValidate({
          isValid,
          errors,
          warnings,
          formData,
          formElement
        });
      }
      if (isValid) {
        if (typeof submitHandler === 'function') {
          submitHandler(e);
        } else {
          const submitter = e.nativeEvent?.submitter;
          const hasSubmitterWithValue = submitter && Boolean(submitter.name) && Boolean(submitter.value);
          if (hasSubmitterWithValue) {
            // add hidden input to form with submit button name and value
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = submitter.name;
            input.value = submitter.value;
            formElement.appendChild(input);
            formElement.submit();
            formElement.removeChild(input);
          } else {
            formElement.submit();
          }
        }
      }
    });
  };

  /**
   * @param {string[]} [names]
   * @returns {void}
   */
  const revalidate = names => {
    setRevalidationQueue(current => {
      if (current === '*' || !names) {
        return '*';
      }
      return [...current, ...names];
    });
  };

  /**
   * Reset the form to its initial state.
   * @returns {void}
   */
  const reset = () => {
    formRef.current.reset();
    setErrors({});
    setWarnings({});
    setHasFormBeenSubmitted(false);
    setRevalidationQueue([]);
  };

  // We run revalidations in an effect to defer them after state and DOM updates.
  // This simplifies the implementation for consumers: it allows them to directly
  // reference React state in validation methods, and means that the FormData
  // object will reflect the user's latest inputs.
  useEffect(() => {
    if (Array.isArray(revalidationQueue) && revalidationQueue.length === 0) {
      return;
    }
    setRevalidationQueue([]);
    const formData = new FormData(formRef.current);
    run(revalidationQueue === '*' ? validations.current : validations.current.filter(v => revalidationQueue.includes(v.name)), formData);
  }, [revalidationQueue, run]);
  return {
    formRef,
    onSubmit,
    onChange,
    onBlur,
    hasError,
    errorMessage,
    errorLevel,
    revalidate,
    reset,
    hasSubmitted: hasFormBeenSubmitted
  };
};
export default useFormValidation;

/**
 * @typedef {(value: string) => boolean} Validator - Returns true if value is valid, otherwise false
 */

/**
 * @typedef {'change' | 'changeAfterSubmit' | 'blur'} ValidationTrigger
 */

/**
 * @typedef {'error' | 'warning'} ErrorLevel
 */

/**
 * @typedef {Object} ValidationInit
 * @property {(string | Validator)} validationMethod - The validation method to be used.
 * @property {string} errorMessage - The error message to be displayed if validation fails.
 * @property {ErrorLevel} [errorLevel] - The severity of the error: "error" or "warning" (defaults to "error"). Errors block submission, while warnings don't.
 * @property {ValidationTrigger | ValidationTrigger[]} [when] - The event or events that trigger validation. The default for errors is "changeAfterSubmit", and "blur" for warnings. All validations, regardless of level or trigger, will run on submit.
 */

/**
 * @typedef {Record<string, ValidationInit[]>} FormValidationSchema - The validation schema for the form.
 */

/**
 * @typedef {object} Validation
 * @property {string} name
 * @property {Validator} validationMethod
 * @property {string} errorMessage
 * @property {ErrorLevel} errorLevel
 * @property {ValidationTrigger[]} when
 */

/**
 * @typedef {Record<string, { disableOnChange?: boolean, disableMultipleErrors?: boolean }>} FormValidationOptions
 */

/**
 * @typedef {object} FormValidationConfig
 * @property {FormValidationSchema} schema
 * @property {ValidationHandler} [onValidate]
 * @property {FormValidationOptions} [options]
 */

/**
 * @typedef {function(event: import('react').FormEvent<HTMLFormElement>, submitHandler?: import('react').FormEventHandler<HTMLFormElement>): void} SubmitEventHandler - The submit event handler.
 */

/**
 * @typedef {object} FormValidationBag
 * @property {import('react').MutableRefObject<HTMLFormElement>} formRef
 * @property {SubmitEventHandler} onSubmit
 * @property {import('react').ChangeEventHandler<HTMLInputElement>} onChange - The change event handler.
 * @property {import('react').FocusEventHandler<HTMLInputElement>} onBlur - The blur event handler.
 * @property {(name: string) => boolean} hasError - The function to check if an input has an error.
 * @property {(name: string) => ErrorLevel} errorLevel - The function to get an input's error level.
 * @property {(name: string) => string | null} errorMessage - The function to get an input's error message.
 * @property {(names?: string[]) => void} revalidate - Run validations for the fields with the given names. If no names are provided, runs validations for all fields.
 * @property {boolean} hasSubmitted - Whether the form has been submitted yet.
 */

/**
 * @typedef {object} ValidationError
 * @property {string} name
 * @property {string} errorMessage
 * @property {ErrorLevel} errorLevel
 * @property {boolean} hasRequiredError
 */

/**
 * @typedef {Record<string, ValidationError | null>} ValidationErrorMap
 */

/**
 * @typedef {Object} ValidationResult
 * @property {boolean} isValid
 * @property {ValidationError[]} errors
 * @property {ValidationError[]} warnings
 * @property {HTMLFormElement} formElement
 * @property {FormData} formData
 */

/**
 * @callback ValidationHandler
 * @param {ValidationResult} result
 * @returns {void}
 */

/**
 * @typedef {Object} ValidationMethod
 * @property {Function} validationMethod - The validation method to check against the input value.
 * @property {string} name - The name of the validation method.
 */